import { Schema } from "leva/dist/declarations/src/types";

import { Renderer } from "../components/renderer/Renderer";
import { DEGREES_TO_RADIANS, RS_TO_RADIANS } from "../rs/MathConstants";
import { SceneBuilder } from "../rs/scene/SceneBuilder";
import { clamp } from "../util/MathUtil";
import { ProjectionType } from "./Camera";
import { getAxisDeadzone } from "./InputManager";
import { MapManager, MapSquare } from "./MapManager";
import { MapViewer } from "./MapViewer";

// Automatic render distance, in tiles: base + per tile of camera height above the ground. The
// default camera (about 24 tiles up) gets the old fixed default of 128.
const AUTO_RENDER_DISTANCE_BASE = 48;
const AUTO_RENDER_DISTANCE_PER_HEIGHT = 3.25;
const AUTO_RENDER_DISTANCE_MIN = 64;
const AUTO_RENDER_DISTANCE_MAX = 512;
// Time constant (ms) for easing towards the target, so the fog edge doesn't jump when the
// ground under the camera changes.
const AUTO_RENDER_DISTANCE_EASE_MS = 250;

export abstract class MapViewerRenderer<T extends MapSquare = MapSquare> extends Renderer {
    mapManager: MapManager<T>;

    // Last known ground height under the camera (fine units), for when that map isn't loaded.
    private lastCameraGroundY: number = 0;

    constructor(public mapViewer: MapViewer) {
        super();
        this.mapManager = new MapManager(
            mapViewer.workerPool.size * 2,
            this.queueLoadMap.bind(this),
        );
    }

    override async init() {
        this.mapViewer.inputManager.init(this.canvas);
    }

    override cleanUp(): void {
        this.mapViewer.inputManager.cleanUp();
    }

    initCache(): void {
        this.mapManager.init(
            this.mapViewer.mapFileIndex,
            SceneBuilder.fillEmptyTerrain(this.mapViewer.loadedCache.info),
        );
        this.mapManager.update(
            this.mapViewer.camera,
            this.stats.frameCount,
            this.mapViewer.renderDistance,
            this.mapViewer.unloadDistance,
        );
    }

    getControls(): Schema {
        return {};
    }

    queueLoadMap(mapX: number, mapY: number): void {}

    setLoadNpcs?(enabled: boolean): void;

    handleInput(deltaTime: number) {
        this.handleKeyInput(deltaTime);
        this.handleMouseInput();
        this.handleControllerInput(deltaTime);
        this.handleJoystickInput(deltaTime);
    }

    handleKeyInput(deltaTime: number) {
        const deltaTimeSec = deltaTime / 1000;

        const inputManager = this.mapViewer.inputManager;
        const camera = this.mapViewer.camera;

        let cameraSpeedMult = 1.0;
        if (inputManager.isShiftDown()) {
            cameraSpeedMult = 10.0;
        }
        if (inputManager.isKeyDown("Tab")) {
            cameraSpeedMult = 0.1;
        }

        const deltaPitch = 64 * 5 * deltaTimeSec;
        const deltaYaw = 64 * 5 * deltaTimeSec;

        // camera direction controls
        if (inputManager.isKeyDown("ArrowUp")) {
            camera.updatePitch(camera.pitch, deltaPitch);
        }
        if (inputManager.isKeyDown("ArrowDown")) {
            camera.updatePitch(camera.pitch, -deltaPitch);
        }
        if (inputManager.isKeyDown("ArrowRight")) {
            camera.updateYaw(camera.yaw, deltaYaw);
        }
        if (inputManager.isKeyDown("ArrowLeft")) {
            camera.updateYaw(camera.yaw, -deltaYaw);
        }

        // camera position controls
        let deltaX = 0;
        let deltaY = 0;
        let deltaZ = 0;

        const deltaPos = 16 * (this.mapViewer.cameraSpeed * cameraSpeedMult) * deltaTimeSec;
        const deltaHeight = 8 * (this.mapViewer.cameraSpeed * cameraSpeedMult) * deltaTimeSec;

        if (inputManager.isKeyDown("KeyW")) {
            // Forward
            deltaZ -= deltaPos;
        }
        if (inputManager.isKeyDown("KeyA")) {
            // Left
            deltaX += deltaPos;
        }
        if (inputManager.isKeyDown("KeyS")) {
            // Back
            deltaZ += deltaPos;
        }
        if (inputManager.isKeyDown("KeyD")) {
            // Right
            deltaX -= deltaPos;
        }
        if (inputManager.isKeyDown("KeyE") || inputManager.isKeyDown("KeyR")) {
            // Move up
            deltaY -= deltaHeight;
        }
        if (
            inputManager.isKeyDown("KeyQ") ||
            inputManager.isKeyDown("KeyC") ||
            inputManager.isKeyDown("KeyF")
        ) {
            // Move down
            deltaY += deltaHeight;
        }

        if (deltaX !== 0 || deltaZ !== 0) {
            camera.move(deltaX, 0, deltaZ);
        }
        if (deltaY !== 0) {
            camera.move(0, deltaY, 0);
        }

        if (inputManager.isKeyDown("KeyP")) {
            camera.pos[0] = 2780;
            camera.pos[2] = 9537;
        }
    }

    // Mouse wheel on the free camera: fly along the view direction, or change the ortho zoom.
    // Positive deltas (scrolling towards the user) zoom out.
    handleWheelInput(wheelDelta: number) {
        if (wheelDelta === 0) {
            return;
        }
        const camera = this.mapViewer.camera;
        if (camera.projectionType === ProjectionType.ORTHO) {
            camera.orthoZoom = clamp(camera.orthoZoom * Math.exp(-wheelDelta * 0.0015), 1, 60);
            camera.updated = true;
        } else {
            let speedMult = this.mapViewer.cameraSpeed;
            if (this.mapViewer.inputManager.isShiftDown()) {
                speedMult *= 10;
            }
            // About 2 tiles per wheel notch.
            camera.move(0, 0, wheelDelta * 0.02 * speedMult, true);
        }
    }

    handleMouseInput() {
        const inputManager = this.mapViewer.inputManager;
        const camera = this.mapViewer.camera;

        if (inputManager.isPointerLock()) {
            this.mapViewer.closeMenu();
        }

        // mouse/touch controls
        const deltaMouseX = inputManager.getDeltaMouseX();
        const deltaMouseY = inputManager.getDeltaMouseY();

        if (deltaMouseX !== 0 || deltaMouseY !== 0) {
            if (inputManager.isTouch) {
                camera.move(0, clamp(-deltaMouseY, -100, 100) * 0.004, 0);
            } else {
                camera.updatePitch(camera.pitch, deltaMouseY * 0.9);
                camera.updateYaw(camera.yaw, deltaMouseX * -0.9);
            }
        }

        const panX = inputManager.getPanDeltaX();
        const panY = inputManager.getPanDeltaY();
        if (panX !== 0 || panY !== 0) {
            this.panCamera(panX, panY);
        }
    }

    // Right drag: slide the camera over the ground so the ground follows the cursor.
    panCamera(deltaX: number, deltaY: number) {
        const camera = this.mapViewer.camera;
        // Ground distance per screen pixel up/down grows as the camera looks closer to level.
        const lookDown = Math.max(Math.sin(-camera.pitch * RS_TO_RADIANS), 0.2);
        let tilesPerPixel: number;
        if (camera.projectionType === ProjectionType.ORTHO) {
            const cssToDevice = this.canvas.width / Math.max(this.canvas.offsetWidth, 1);
            tilesPerPixel = (2 / camera.orthoZoom) * cssToDevice;
        } else {
            const groundY = this.getGroundHeight(camera.getPosX(), camera.getPosZ()) ?? 0;
            const height = Math.max(groundY / 128 - camera.getPosY(), 1);
            const depth = Math.min(height / lookDown, 256);
            const viewHeight = 2 * depth * Math.tan((camera.fov * DEGREES_TO_RADIANS) / 2);
            tilesPerPixel = viewHeight / Math.max(this.canvas.offsetHeight, 1);
        }
        camera.move(deltaX * tilesPerPixel, 0, (-deltaY * tilesPerPixel) / lookDown);
    }

    // Ground height (fine units, negative is up) at a tile position, if that map is loaded.
    getGroundHeight(tileX: number, tileZ: number): number | undefined {
        return undefined;
    }

    // Zooming out sees further: the render distance grows with the camera's height above the
    // ground (perspective) or with the area on screen (ortho).
    updateRenderDistance(deltaTime: number) {
        const mapViewer = this.mapViewer;
        const camera = mapViewer.camera;
        const groundY = this.getGroundHeight(camera.getPosX(), camera.getPosZ());
        if (groundY !== undefined) {
            this.lastCameraGroundY = groundY;
        }
        const height = Math.max(this.lastCameraGroundY / 128 - camera.getPosY(), 0);

        let target: number;
        if (camera.projectionType === ProjectionType.ORTHO) {
            // Half the view is width / zoom tiles across, and the ground is stretched along the
            // view by the tilt. The view is centred where the camera ray meets the ground.
            const lookDown = Math.max(Math.sin(-camera.pitch * RS_TO_RADIANS), 0.25);
            const lookAhead = (height * Math.sqrt(1 - lookDown * lookDown)) / lookDown;
            const halfView =
                Math.max(this.canvas.width, this.canvas.height / lookDown) / camera.orthoZoom;
            target = halfView + lookAhead;
        } else {
            target = AUTO_RENDER_DISTANCE_BASE + height * AUTO_RENDER_DISTANCE_PER_HEIGHT;
        }
        target = clamp(target, AUTO_RENDER_DISTANCE_MIN, AUTO_RENDER_DISTANCE_MAX);

        const ease = 1 - Math.exp(-deltaTime / AUTO_RENDER_DISTANCE_EASE_MS);
        mapViewer.renderDistance += (target - mapViewer.renderDistance) * ease;
    }

    handleControllerInput(deltaTime: number) {
        const deltaPitch = deltaTime;
        const deltaYaw = deltaTime;

        const inputManager = this.mapViewer.inputManager;
        const camera = this.mapViewer.camera;

        // controller
        const gamepad = inputManager.getGamepad();

        if (gamepad && gamepad.connected && gamepad.mapping === "standard") {
            let cameraSpeedMult = 0.01;
            // X, R1
            if (gamepad.buttons[0].pressed || gamepad.buttons[5].pressed) {
                cameraSpeedMult = 0.1;
            }

            const zone = 0.1;

            const leftX = getAxisDeadzone(gamepad.axes[0], zone);
            const leftY = getAxisDeadzone(-gamepad.axes[1], zone);
            const leftTrigger = gamepad.buttons[6].value;

            const rightX = getAxisDeadzone(gamepad.axes[2], zone);
            const rightY = getAxisDeadzone(-gamepad.axes[3], zone);
            const rightTrigger = gamepad.buttons[7].value;

            const trigger = leftTrigger - rightTrigger;

            if (leftX !== 0 || leftY !== 0 || trigger !== 0) {
                camera.move(
                    leftX * cameraSpeedMult * -deltaTime,
                    0,
                    leftY * cameraSpeedMult * -deltaTime,
                    false,
                );
                camera.move(0, trigger * cameraSpeedMult * -deltaTime, 0);
            }

            if (rightX !== 0) {
                camera.updateYaw(camera.yaw, deltaYaw * 1.5 * rightX);
            }
            if (rightY !== 0) {
                camera.updatePitch(camera.pitch, deltaPitch * 1.5 * rightY);
            }
        }
    }

    handleJoystickInput(deltaTime: number) {
        const deltaTimeSec = deltaTime / 1000;

        const inputManager = this.mapViewer.inputManager;
        const camera = this.mapViewer.camera;

        const deltaPitch = 64 * 5 * deltaTimeSec;
        const deltaYaw = 64 * 5 * deltaTimeSec;

        // joystick controls
        const positionJoystickEvent = inputManager.positionJoystickEvent;
        const cameraJoystickEvent = inputManager.cameraJoystickEvent;

        if (positionJoystickEvent) {
            const moveX = positionJoystickEvent.x ?? 0;
            const moveY = positionJoystickEvent.y ?? 0;

            camera.move(moveX * 32 * -deltaTimeSec, 0, moveY * 32 * -deltaTimeSec);
        }

        if (cameraJoystickEvent) {
            const moveX = cameraJoystickEvent.x ?? 0;
            const moveY = cameraJoystickEvent.y ?? 0;
            camera.updatePitch(camera.pitch, deltaPitch * 1.5 * moveY);
            camera.updateYaw(camera.yaw, deltaYaw * 1.5 * moveX);
        }
    }

    override onFrameEnd(): void {
        super.onFrameEnd();

        if (window.wallpaperFpsLimit !== undefined) {
            this.fpsLimit = window.wallpaperFpsLimit;
        }

        if (this.mapViewer.camera.updated) {
            this.mapViewer.updateSearchParams();
        }

        this.mapViewer.inputManager.onFrameEnd();
        this.mapViewer.camera.onFrameEnd();

        // this.mapViewer.debugText = `Frame Time Js: ${this.stats.frameTimeJs.toFixed(3)}`;
    }
}
